/**
 * スレッド会話コンポーネント
 * メッセージ履歴表示と返信機能
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Send as SendIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  ThreadDetail,
  SenderType,
  ThreadStatus,
} from '../../types/message-thread';
import { MessageBubble } from './MessageBubble';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

interface ThreadConversationProps {
  threadDetail: ThreadDetail | null;
  loading: boolean;
  sending: boolean;
  onSendMessage: (messageBody: string) => Promise<void>;
  userType: 'company' | 'student';
  canReply: boolean;
}

export const ThreadConversation: React.FC<ThreadConversationProps> = ({
  threadDetail,
  loading,
  sending,
  onSendMessage,
  userType,
  canReply,
}) => {
  const [messageBody, setMessageBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // メッセージ送信
  const handleSendMessage = async () => {
    if (!messageBody.trim()) return;

    try {
      setError(null);
      await onSendMessage(messageBody.trim());
      setMessageBody('');
    } catch (error: any) {
      setError(
        error?.response?.data?.message || 'メッセージの送信に失敗しました'
      );
    }
  };

  // Enterキーでの送信
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // 最新メッセージまでスクロール
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (threadDetail?.messages.length) {
      scrollToBottom();
    }
  }, [threadDetail?.messages.length]);

  // 実際の表示用ステータスを計算（学生・企業の個別フラグを考慮）
  const getDisplayStatus = (thread: any) => {
    if (userType === 'student') {
      if (thread.studentBlocked) return ThreadStatus.BLOCKED;
      if (thread.studentArchived) return ThreadStatus.ARCHIVED;
    } else {
      if (thread.companyArchived) return ThreadStatus.ARCHIVED;
    }
    return thread.status;
  };

  const getStatusColor = (thread: any) => {
    const displayStatus = getDisplayStatus(thread);
    switch (displayStatus) {
      case ThreadStatus.ACTIVE:
        return 'success';
      case ThreadStatus.ARCHIVED:
        return 'default';
      case ThreadStatus.BLOCKED:
        return 'error';
      case ThreadStatus.CLOSED:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (thread: any) => {
    const displayStatus = getDisplayStatus(thread);
    switch (displayStatus) {
      case ThreadStatus.ACTIVE:
        return 'アクティブ';
      case ThreadStatus.ARCHIVED:
        return 'アーカイブ';
      case ThreadStatus.BLOCKED:
        return 'ブロック';
      case ThreadStatus.CLOSED:
        return 'クローズ';
      default:
        return displayStatus;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!threadDetail) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant="h6">スレッドを選択してください</Typography>
      </Box>
    );
  }

  const { thread, messages } = threadDetail;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Tooltip
            title={
              userType === 'student'
                ? thread.company?.name || '企業詳細を表示'
                : thread.student?.name || undefined
            }
          >
            <Avatar
              src={
                userType === 'student'
                  ? thread.company?.logoUrl || undefined
                  : thread.student?.avatarUrl || undefined
              }
              alt={
                userType === 'student'
                  ? thread.company?.name || '企業'
                  : thread.student?.name || '学生'
              }
              sx={{
                cursor:
                  userType === 'student' && thread.company
                    ? 'pointer'
                    : 'default',
              }}
              onClick={() =>
                userType === 'student' && thread.company
                  ? navigate(`/companies/${thread.company.id}/profile`)
                  : undefined
              }
            >
              {userType === 'student' ? <BusinessIcon /> : <PersonIcon />}
            </Avatar>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {thread.subject}
            </Typography>
            {userType === 'student' && thread.company ? (
              <Link
                component="button"
                variant="body2"
                color="text.secondary"
                underline="hover"
                sx={{
                  textAlign: 'left',
                  px: 0,
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
                onClick={() =>
                  navigate(`/companies/${thread.company.id}/profile`)
                }
              >
                {thread.company.name}
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {userType === 'company'
                  ? thread.student?.name || '学生'
                  : 'あなた'}
              </Typography>
            )}
          </Box>
          <Chip
            size="small"
            label={getStatusText(thread)}
            color={getStatusColor(thread)}
            variant="outlined"
          />
        </Box>

        {/* スレッド情報 */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            作成日:{' '}
            {format(new Date(thread.createdAt), 'yyyy/MM/dd HH:mm', {
              locale: ja,
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            メッセージ数: {thread.messageCount}件
          </Typography>
          {threadDetail.unreadCount > 0 && (
            <Chip
              size="small"
              label={`未読: ${threadDetail.unreadCount}件`}
              color="error"
              variant="filled"
            />
          )}
        </Box>
      </Box>

      {/* メッセージ一覧 */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: { xs: 0.5, sm: 1 },
          py: 2,
          backgroundColor: 'grey.50',
        }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body1">まだメッセージがありません</Typography>
          </Box>
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={
                  (userType === 'company' &&
                    message.senderType === SenderType.COMPANY) ||
                  (userType === 'student' &&
                    message.senderType === SenderType.STUDENT)
                }
                showAvatar={
                  index === 0 ||
                  messages[index - 1].senderType !== message.senderType
                }
                userType={userType}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* 返信エリア */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'white',
          minHeight: 'auto',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!canReply ? (
          <Alert severity="info" icon={<InfoIcon />}>
            このスレッドには返信できません
          </Alert>
        ) : userType === 'student' &&
          getDisplayStatus(thread) === ThreadStatus.BLOCKED ? (
          <Alert severity="info" icon={<InfoIcon />}>
            このスレッドをブロックしています。返信はできません。
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              placeholder="メッセージを入力してください..."
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sending}
              variant="outlined"
              size="small"
            />
            <Button
              variant="contained"
              endIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSendMessage}
              disabled={!messageBody.trim() || sending}
              sx={{ minWidth: 100 }}
            >
              {sending ? '送信中' : '送信'}
            </Button>
          </Box>
        )}

        {/* 文字数カウント */}
        {messageBody && (
          <Typography
            variant="caption"
            color={messageBody.length > 1000 ? 'error' : 'text.secondary'}
            sx={{ mt: 1, display: 'block', textAlign: 'right' }}
          >
            {messageBody.length}/1000文字
          </Typography>
        )}
      </Box>
    </Box>
  );
};
